<script setup lang="ts">
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import UserResource from '@/api/resources/user'
import router from '@/router'
import { disconnectActionCable } from '@/services/actioncable.service'
import { clearAllStores } from '@/services/clear-stores'
import { useSessionStore } from '@/store/session'
import { useUserStore } from '@/store/user'
import { translateUserRole } from '@/interfaces/enums'
import { logFirebaseAnalytics } from '@/utils/firebase'
import { ArrowRightStartOnRectangleIcon } from '@heroicons/vue/24/outline'
import { computed, ref } from 'vue'

const DEFAULT_AVATAR_URL = new URL('@/assets/images/user/placeholder_avatar.jpg', import.meta.url).href
const avatarSrc = computed(() => currentUser.avatar_url || DEFAULT_AVATAR_URL)
const isConfirmLogoutOpen = ref(false)
const props = withDefaults(
  defineProps<{
    compact?: boolean
  }>(),
  {
    compact: false,
  },
)

const logoutHandler = async () => {
  logFirebaseAnalytics('logout', { time: new Date().toISOString() })
  await UserResource.signOut().catch(() => {})
  disconnectActionCable()
  useSessionStore().destroySession()
  clearAllStores()
  isConfirmLogoutOpen.value = false
}

const openProfilePage = () => {
  logFirebaseAnalytics('profile', { time: new Date().toISOString() })
  router.push({ name: 'user-profile' })
}

const currentUser = useUserStore().currentUser
</script>

<template>
  <div>
    <div
      class="flex items-center overflow-hidden text-white"
      :class="props.compact ? 'w-10 justify-start' : 'w-full justify-between border border-white/20 bg-white/5 hover:bg-white/10'">
      <button
        class="flex items-center min-w-0"
        :class="props.compact ? 'h-10 w-10 border border-white/20 bg-white/5 p-0 hover:bg-white/10' : 'flex-1 gap-3 px-3 py-2'"
        @click="openProfilePage">
        <span class="h-10 w-10 overflow-hidden border border-white/20">
          <img class="h-full w-full object-cover" :src="avatarSrc" alt="Foto do usuário" @error="($event.target as HTMLImageElement).src = DEFAULT_AVATAR_URL" />
        </span>

        <span v-if="!props.compact" class="max-w-[150px] text-left">
          <span class="block truncate whitespace-nowrap text-sm font-medium text-white">{{ currentUser.name }}</span>
          <span class="block text-xs font-medium text-slate-300">{{ translateUserRole(currentUser.role) }}</span>
        </span>
      </button>

      <button
        v-if="!props.compact"
        class="shrink-0 border-l border-white/20 px-3 py-2 text-white hover:bg-white/10"
        title="Sair da aplicação"
        @click="isConfirmLogoutOpen = true">
        <ArrowRightStartOnRectangleIcon class="h-5 w-5" />
      </button>
    </div>

    <ConfirmationModal
      :is-open="isConfirmLogoutOpen"
      cancel-text="Cancelar"
      confirmation-text="Sair"
      @cancel="isConfirmLogoutOpen = false"
      @add="logoutHandler">
      <template #content>
        <div class="space-y-2">
          <h3 class="text-lg font-semibold text-slate-900">Deseja realmente sair?</h3>
          <p class="text-sm text-slate-600">Você precisará fazer login novamente para continuar usando o sistema.</p>
        </div>
      </template>
    </ConfirmationModal>
  </div>
</template>
